<template>
  <b-card no-body class="rci-card mt-2 mb-2">
    <b-card-header class="rci-card-header">
      <div
        class="d-flex align-items-center justify-content-between w-100 flex-wrap"
      >
        <div class="d-flex align-items-center mb-50 mb-md-0">
          <div class="rci-header-icon mr-1">
            <span class="rci-header-icon__ring"></span>
            <feather-icon icon="BarChart2Icon" size="22" />
          </div>
          <div>
            <div class="d-flex align-items-center flex-wrap">
              <h4 class="mb-0 rci-title">Result Comparison</h4>
              <span v-if="demoMode" class="rci-status-pill rci-status-pill--demo ml-50">
                <feather-icon icon="EyeIcon" size="11" class="mr-25" />
                Demo data
              </span>
              <span v-else class="rci-status-pill rci-status-pill--live ml-50">
                <feather-icon icon="ActivityIcon" size="11" class="mr-25" />
                Live
              </span>
            </div>
            <small class="rci-subtitle">
              <feather-icon icon="LayersIcon" size="12" class="mr-25" />
              Compare class, exam, session &amp; campus performance
            </small>
          </div>
        </div>
        <div class="rci-header-meta">
          <span v-if="lastRefreshed" class="rci-meta-chip">
            <feather-icon icon="ClockIcon" size="12" class="mr-25" />
            Updated {{ lastRefreshed }}
          </span>
          <span class="rci-meta-chip rci-meta-chip--mode">
            <feather-icon :icon="activeModeIcon" size="12" class="mr-25" />
            {{ activeModeLabel }} mode
          </span>
        </div>
      </div>
    </b-card-header>

    <b-card-body class="rci-body">
      <!-- Mode tabs -->
      <div class="rci-mode-switch mb-2">
        <button
          v-for="m in visibleModes"
          :key="m.id"
          type="button"
          class="rci-mode-btn"
          :class="{ 'rci-mode-btn--active': mode === m.id }"
          @click="switchMode(m.id)"
        >
          <span class="rci-mode-btn__icon">
            <feather-icon :icon="m.icon" size="15" />
          </span>
          <span class="rci-mode-btn__label">{{ m.label }}</span>
        </button>
      </div>

      <!-- Filters -->
      <div class="rci-filter-bar mb-2">
        <div class="rci-filter-bar__head">
          <div class="rci-filter-bar__title">
            <feather-icon icon="FilterIcon" size="14" class="mr-50" />
            Comparison filters
          </div>
          <small class="rci-filter-bar__hint">
            Pick what to compare, then run analysis
          </small>
        </div>

        <div class="rci-filter-bar__grid">
          <div
            v-if="mode === 'class' || mode === 'exam' || mode === 'session'"
            class="rci-filter-item rci-filter-item--grow"
          >
            <label class="rci-filter-label">
              <feather-icon icon="UsersIcon" size="12" />
              Classes
            </label>
            <v-select
              v-model="selectedClassIDs"
              :options="classOptions"
              label="name"
              :reduce="(o) => o.id"
              multiple
              :close-on-select="false"
              placeholder="Select classes"
              :loading="lookupsLoading"
              class="rci-select"
            />
          </div>

          <div
            v-if="mode === 'campus'"
            class="rci-filter-item rci-filter-item--grow"
          >
            <label class="rci-filter-label">
              <feather-icon icon="MapPinIcon" size="12" />
              Campuses
            </label>
            <v-select
              v-model="selectedCampusIDs"
              :options="campusOptions"
              label="branch"
              :reduce="(o) => o.id"
              multiple
              :close-on-select="false"
              placeholder="Select campuses"
              :loading="campusesLoading"
              class="rci-select"
            />
          </div>

          <div v-if="mode === 'campus'" class="rci-filter-item">
            <label class="rci-filter-label">
              <feather-icon icon="UsersIcon" size="12" />
              Class
            </label>
            <v-select
              v-model="selectedClassIDs"
              :options="classOptions"
              label="name"
              :reduce="(o) => o.id"
              multiple
              :close-on-select="false"
              placeholder="Classes"
              :loading="lookupsLoading"
              class="rci-select"
            />
          </div>

          <div v-if="mode !== 'exam'" class="rci-filter-item">
            <label class="rci-filter-label">
              <feather-icon icon="FileTextIcon" size="12" />
              Exam type
            </label>
            <v-select
              v-model="selectedExamTypeIDs"
              :options="examTypeOptions"
              :label="examTypeLabelKey"
              :reduce="(o) => o.id"
              :multiple="false"
              placeholder="Exam type"
              :loading="lookupsLoading"
              class="rci-select"
            />
          </div>

          <div v-else class="rci-filter-item rci-filter-item--grow">
            <label class="rci-filter-label">
              <feather-icon icon="FileTextIcon" size="12" />
              Exam types
            </label>
            <v-select
              v-model="selectedExamTypeIDs"
              :options="examTypeOptions"
              :label="examTypeLabelKey"
              :reduce="(o) => o.id"
              multiple
              :close-on-select="false"
              placeholder="Select exam types"
              :loading="lookupsLoading"
              class="rci-select"
            />
          </div>

          <div v-if="mode !== 'session'" class="rci-filter-item">
            <label class="rci-filter-label">
              <feather-icon icon="CalendarIcon" size="12" />
              Session
            </label>
            <v-select
              v-model="selectedSessionIDs"
              :options="sessionOptions"
              :label="sessionLabelKey"
              :reduce="(o) => o.id"
              :multiple="false"
              placeholder="Session"
              :loading="lookupsLoading"
              class="rci-select"
            />
          </div>

          <div v-else class="rci-filter-item rci-filter-item--grow">
            <label class="rci-filter-label">
              <feather-icon icon="CalendarIcon" size="12" />
              Sessions
            </label>
            <v-select
              v-model="selectedSessionIDs"
              :options="sessionOptions"
              :label="sessionLabelKey"
              :reduce="(o) => o.id"
              multiple
              :close-on-select="false"
              placeholder="Select sessions"
              :loading="lookupsLoading"
              class="rci-select"
            />
          </div>

          <div class="rci-filter-item">
            <label class="rci-filter-label">
              <feather-icon icon="BookOpenIcon" size="12" />
              Subjects
            </label>
            <v-select
              v-model="selectedSubjectIDs"
              :options="subjectOptions"
              label="name"
              :reduce="(o) => o.id"
              multiple
              :close-on-select="false"
              placeholder="All subjects"
              :loading="subjectsLoading"
              class="rci-select"
            />
          </div>

          <div class="rci-filter-item rci-filter-item--action">
            <label class="rci-filter-label rci-filter-label--spacer">&nbsp;</label>
            <b-button
              variant="primary"
              class="rci-compare-btn"
              :disabled="!canCompare || loading"
              @click="runCompare"
            >
              <b-spinner v-if="loading" small class="mr-50" />
              <feather-icon v-else icon="SlidersIcon" size="15" class="mr-50" />
              Compare
            </b-button>
          </div>
        </div>
      </div>

      <p v-if="validationHint && !hasResult" class="rci-hint mb-2">
        <feather-icon icon="AlertTriangleIcon" size="13" class="mr-50" />
        {{ validationHint }}
      </p>

      <!-- Loading -->
      <div v-if="loading" class="rci-skeleton py-2">
        <b-row class="mb-2">
          <b-col
            v-for="i in 5"
            :key="'kpi-sk-' + i"
            cols="6"
            md="2"
            class="mb-1"
          >
            <div class="rci-skel-block" style="height: 92px"></div>
          </b-col>
        </b-row>
        <div class="rci-skel-block mb-2" style="height: 280px"></div>
        <div class="rci-skel-block" style="height: 160px"></div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="rci-state rci-state--error">
        <div class="rci-state__icon">
          <feather-icon icon="AlertCircleIcon" size="26" />
        </div>
        <p class="rci-state__title">{{ error }}</p>
        <p class="rci-state__sub">Something went wrong while loading comparison.</p>
        <b-button size="sm" variant="outline-primary" @click="runCompare">
          <feather-icon icon="RefreshCwIcon" size="13" class="mr-50" />
          Retry
        </b-button>
      </div>

      <!-- Empty -->
      <div v-else-if="!hasResult" class="rci-state rci-state--empty">
        <div class="rci-state__icon">
          <feather-icon icon="SlidersIcon" size="26" />
        </div>
        <p class="rci-state__title">Ready to compare results</p>
        <p class="rci-state__sub">{{ emptyCta }}</p>
        <div class="rci-state__steps">
          <span><feather-icon icon="CheckSquareIcon" size="12" /> Select filters</span>
          <span><feather-icon icon="PlayIcon" size="12" /> Run compare</span>
          <span><feather-icon icon="PieChartIcon" size="12" /> Review insights</span>
        </div>
      </div>

      <!-- Results -->
      <div v-else class="rci-results rci-anim-in">
        <div class="rci-summary-row">
          <div class="d-flex align-items-start">
            <div class="rci-summary-icon mr-1">
              <feather-icon :icon="activeModeIcon" size="18" />
            </div>
            <div>
              <h5 class="rci-summary-title mb-25">{{ reportHeadline }}</h5>
              <p class="rci-summary-context mb-0">
                <feather-icon icon="InfoIcon" size="12" class="mr-25" />
                {{ reportContextLine }}
              </p>
            </div>
          </div>
          <div class="rci-summary-meta">
            <span class="rci-chip">
              <feather-icon icon="BookIcon" size="12" class="mr-25" />
              {{ tableRows.length }} subjects
            </span>
            <span class="rci-chip">
              <feather-icon icon="LayersIcon" size="12" class="mr-25" />
              {{ dimensions.length }} compared
            </span>
          </div>
        </div>

        <div class="rci-kpi-grid">
          <div
            v-for="(card, idx) in scoreCards"
            :key="'score-' + card.key"
            class="rci-kpi-card"
            :class="'rci-kpi-card--' + card.tone"
            :style="{ animationDelay: idx * 50 + 'ms' }"
          >
            <div
              class="rci-kpi-card__icon"
              :class="'rci-kpi-card__icon--' + card.tone"
            >
              <feather-icon :icon="card.icon" size="17" />
            </div>
            <div class="rci-kpi-card__body">
              <div class="rci-kpi-card__label">{{ card.label }}</div>
              <div class="rci-kpi-card__value">{{ card.value }}</div>
              <div class="rci-kpi-card__hint">{{ card.hint }}</div>
            </div>
          </div>
        </div>

        <div v-if="smartInsights.length" class="rci-insights-block">
          <div class="rci-insights-block__head">
            <feather-icon icon="ZapIcon" size="14" class="mr-50" />
            Smart insights
          </div>
          <div class="rci-insights-grid">
            <div
              v-for="(tip, idx) in smartInsights"
              :key="'insight-' + idx"
              class="rci-insight-card"
              :class="'rci-insight-card--' + tip.tone"
              :style="{ animationDelay: 80 + idx * 60 + 'ms' }"
            >
              <div class="rci-insight-card__icon">
                <feather-icon :icon="tip.icon" size="15" />
              </div>
              <div>
                <div class="rci-insight-card__title">{{ tip.title }}</div>
                <div class="rci-insight-card__text">{{ tip.text }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="rci-charts-grid">
          <div class="rci-panel rci-anim-rise">
            <div class="rci-panel__head">
              <div class="d-flex align-items-center">
                <div class="rci-panel__badge rci-panel__badge--primary mr-75">
                  <feather-icon icon="BarChart2Icon" size="15" />
                </div>
                <div>
                  <h6 class="rci-panel__title mb-0">Subject averages</h6>
                  <small class="rci-panel__sub">{{ modeChartSubtitle }}</small>
                </div>
              </div>
            </div>
            <div v-if="seriesLegendItems.length" class="rci-series-legend">
              <span
                v-for="item in seriesLegendItems"
                :key="'leg-' + item.name"
                class="rci-series-chip"
                :title="item.name"
              >
                <span
                  class="rci-series-chip__dot"
                  :style="{ background: item.color }"
                ></span>
                <span class="rci-series-chip__label">{{ item.short }}</span>
              </span>
            </div>
            <vue-apex-charts
              type="bar"
              height="300"
              :options="barOptions"
              :series="barSeries"
            />
          </div>
          <div class="rci-panel rci-anim-rise rci-anim-rise--delay">
            <div class="rci-panel__head">
              <div class="d-flex align-items-center">
                <div class="rci-panel__badge rci-panel__badge--success mr-75">
                  <feather-icon icon="TrendingUpIcon" size="15" />
                </div>
                <div>
                  <h6 class="rci-panel__title mb-0">Pass rate ranking</h6>
                  <small class="rci-panel__sub"
                    >Sorted highest → lowest pass %</small
                  >
                </div>
              </div>
            </div>
            <vue-apex-charts
              type="bar"
              height="280"
              :options="rankOptions"
              :series="rankSeries"
            />
          </div>
        </div>

        <div class="rci-panel rci-panel--table rci-anim-rise rci-anim-rise--delay2">
          <div class="rci-panel__head rci-panel__head--table">
            <div class="d-flex align-items-center justify-content-between w-100 flex-wrap">
              <div class="d-flex align-items-center">
                <div class="rci-panel__badge rci-panel__badge--ink mr-75">
                  <feather-icon icon="GridIcon" size="15" />
                </div>
                <div>
                  <h6 class="rci-panel__title mb-0">Subject breakdown</h6>
                  <small class="rci-panel__sub">Avg and pass % by subject</small>
                </div>
              </div>
              <span class="rci-chip rci-chip--soft mt-50 mt-md-0">
                <feather-icon icon="ListIcon" size="12" class="mr-25" />
                Detailed matrix
              </span>
            </div>
          </div>
          <div class="table-responsive rci-table-scroll">
            <table class="rci-table">
              <thead>
                <tr>
                  <th class="rci-sticky-col">
                    <span class="rci-th-with-icon">
                      <feather-icon icon="BookOpenIcon" size="12" />
                      Subject
                    </span>
                  </th>
                  <th
                    v-for="dim in dimensions"
                    :key="'th-' + dim"
                    colspan="2"
                    class="text-center"
                  >
                    <span class="rci-th-with-icon rci-th-with-icon--center">
                      <feather-icon :icon="activeModeIcon" size="12" />
                      {{ dim }}
                    </span>
                  </th>
                </tr>
                <tr>
                  <th class="rci-sticky-col rci-subhead"></th>
                  <template v-for="dim in dimensions">
                    <th :key="'avg-' + dim" class="rci-subhead text-center">
                      <span class="rci-th-with-icon rci-th-with-icon--center">
                        <feather-icon icon="HashIcon" size="11" />
                        Avg
                      </span>
                    </th>
                    <th :key="'pass-' + dim" class="rci-subhead text-center">
                      <span class="rci-th-with-icon rci-th-with-icon--center">
                        <feather-icon icon="CheckCircleIcon" size="11" />
                        Pass %
                      </span>
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rIdx) in tableRows"
                  :key="'row-' + row.subjectID"
                  :style="{ animationDelay: 120 + rIdx * 35 + 'ms' }"
                  class="rci-table-row"
                >
                  <td class="rci-sticky-col">
                    <span class="rci-subject-cell">
                      <span class="rci-subject-dot">
                        <feather-icon icon="BookmarkIcon" size="11" />
                      </span>
                      <span class="rci-subject-name">{{ row.subjectName }}</span>
                    </span>
                  </td>
                  <template v-for="dim in dimensions">
                    <td
                      :key="'a-' + row.subjectID + '-' + dim"
                      class="text-center rci-avg-cell"
                    >
                      {{ formatNum(cellValue(row, dim, "average")) }}
                    </td>
                    <td
                      :key="'p-' + row.subjectID + '-' + dim"
                      class="text-center"
                    >
                      <span
                        class="rci-pass-pill"
                        :class="
                          passBandClass(cellValue(row, dim, 'passPercent'))
                        "
                      >
                        {{ formatNum(cellValue(row, dim, "passPercent")) }}%
                      </span>
                    </td>
                  </template>
                </tr>
                <tr v-if="!tableRows.length">
                  <td
                    :colspan="1 + dimensions.length * 2"
                    class="text-center text-muted py-3"
                  >
                    <feather-icon icon="InboxIcon" size="16" class="mr-50" />
                    No subject rows returned.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import VueApexCharts from "vue-apexcharts";
import { mapActions } from "vuex";
import {
  buildGroupedBarOptions,
  buildPassRankBarOptions,
  toGroupedBarSeries,
  toPassRankSeries,
  SERIES_COLORS,
} from "./resultComparisonCharts.js";

/** Demo/mock data disabled — live API only */
const DEMO_MODE = false;

const DEMO_CLASSES = [
  { id: 101, name: "Class 9-A" },
  { id: 102, name: "Class 9-B" },
  { id: 103, name: "Class 9-C" },
  { id: 104, name: "Class 10-A" },
];
const DEMO_EXAMS = [
  { id: 1, examType: "1st Term Examination", name: "1st Term Examination" },
  { id: 2, examType: "Mid Term", name: "Mid Term" },
  { id: 3, examType: "Final Term", name: "Final Term" },
];
const DEMO_SESSIONS = [
  { id: 11, session: "2025-26", name: "2025-26", isCurrent: true },
  { id: 10, session: "2024-25", name: "2024-25" },
  { id: 9, session: "2023-24", name: "2023-24" },
];
const DEMO_SUBJECTS = [
  { id: 1, name: "English" },
  { id: 2, name: "Math" },
  { id: 3, name: "Science" },
  { id: 4, name: "Urdu" },
  { id: 5, name: "Islamiat" },
  { id: 6, name: "Computer" },
];
const DEMO_CAMPUSES = [
  { id: 1, branch: "North Karachi" },
  { id: 2, branch: "Gulshan" },
  { id: 3, branch: "Malir" },
];

const MODES = [
  { id: "class", label: "Class", icon: "UsersIcon" },
  { id: "exam", label: "Exam type", icon: "FileTextIcon" },
  { id: "session", label: "Session", icon: "CalendarIcon" },
  { id: "campus", label: "Campus", icon: "MapPinIcon", hoOnly: true },
];

function emptyKpis() {
  return {
    overallAverage: 0,
    passPercent: 0,
    studentCount: 0,
    topLabel: "",
    bottomLabel: "",
  };
}

export default {
  name: "ResultComparisonInsights",
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    vSelect,
    VueApexCharts,
  },
  props: {
    campusID: { type: [Number, String], default: 0 },
    isHeadOffice: { type: Boolean, default: false },
    campuses: { type: Array, default: () => [] },
  },
  data() {
    return {
      demoMode: DEMO_MODE,
      mode: "class",
      lookupsLoading: false,
      campusesLoading: false,
      loading: false,
      error: null,
      hasResult: false,
      lastRefreshed: "",
      localCampuses: [],
      classOptions: [],
      examTypeOptions: [],
      sessionOptions: [],
      subjectOptions: [],
      campusSubjectPool: [],
      subjectsLoading: false,
      selectedClassIDs: [],
      selectedExamTypeIDs: null,
      selectedSessionIDs: null,
      selectedCampusIDs: [],
      selectedSubjectIDs: [],
      dimensions: [],
      subjects: [],
      kpis: emptyKpis(),
      insights: [],
    };
  },
  computed: {
    canShowCampusMode() {
      // Campus comparison only for Head Office or campus id 1
      const cId = Number(this.campusID ?? this.$store.state.userData?.cId ?? 0);
      return !!(this.isHeadOffice || cId === 1);
    },
    visibleModes() {
      return MODES.filter((m) => !m.hoOnly || this.canShowCampusMode);
    },
    activeModeMeta() {
      return (
        this.visibleModes.find((m) => m.id === this.mode) ||
        MODES.find((m) => m.id === this.mode) ||
        MODES[0]
      );
    },
    activeModeIcon() {
      return this.activeModeMeta.icon || "BarChart2Icon";
    },
    activeModeLabel() {
      return this.activeModeMeta.label || "Class";
    },
    campusOptions() {
      const source =
        Array.isArray(this.campuses) && this.campuses.length
          ? this.campuses
          : this.localCampuses;
      return (source || [])
        .filter((c) => c && Number(c.id) > 0)
        .map((c) => ({
          ...c,
          branch: c.branch || c.name || `Campus ${c.id}`,
        }));
    },
    examTypeLabelKey() {
      const sample = this.examTypeOptions[0];
      if (sample && sample.examType != null) return "examType";
      if (sample && sample.name != null) return "name";
      return "examType";
    },
    sessionLabelKey() {
      const sample = this.sessionOptions[0];
      if (sample && sample.session != null) return "session";
      if (sample && sample.name != null) return "name";
      return "session";
    },
    canCompare() {
      return !this.validationHint;
    },
    validationHint() {
      const classes = this.asIdArray(this.selectedClassIDs);
      const exams = this.asIdArray(this.selectedExamTypeIDs);
      const sessions = this.asIdArray(this.selectedSessionIDs);
      const campuses = this.asIdArray(this.selectedCampusIDs);

      if (this.mode === "class") {
        if (classes.length < 2) return "Select at least 2 classes to compare.";
        if (classes.length > 6) return "Select at most 6 classes for readable charts.";
        if (!exams.length) return "Select an exam type.";
        if (!sessions.length) return "Select a session.";
      }
      if (this.mode === "exam") {
        if (!classes.length) return "Select at least 1 class.";
        if (exams.length < 2) return "Select at least 2 exam types.";
        if (exams.length > 6) return "Select at most 6 exam types.";
        if (!sessions.length) return "Select a session.";
      }
      if (this.mode === "session") {
        if (!classes.length) return "Select at least 1 class.";
        if (!exams.length) return "Select an exam type.";
        if (sessions.length < 2) return "Select at least 2 sessions.";
        if (sessions.length > 6) return "Select at most 6 sessions.";
      }
      if (this.mode === "campus") {
        if (campuses.length < 2) return "Select at least 2 campuses.";
        if (campuses.length > 6) return "Select at most 6 campuses.";
        if (!classes.length) return "Select at least 1 class.";
        if (!exams.length) return "Select an exam type.";
        if (!sessions.length) return "Select a session.";
      }
      return "";
    },
    emptyCta() {
      if (this.mode === "class")
        return "Select at least 2 classes, an exam type and session, then Compare.";
      if (this.mode === "exam")
        return "Select a class and at least 2 exam types, then Compare.";
      if (this.mode === "session")
        return "Select a class, exam type and at least 2 sessions, then Compare.";
      return "Select at least 2 campuses, class, exam and session, then Compare.";
    },
    modeChartSubtitle() {
      if (this.mode === "class")
        return "Each bar group is a subject - colours = classes";
      if (this.mode === "exam")
        return "Each bar group is a subject - colours = exam types";
      if (this.mode === "session")
        return "Each bar group is a subject - colours = sessions";
      return "Each bar group is a subject - colours = campuses";
    },
    reportHeadline() {
      if (this.mode === "class") return "Class-wise result comparison";
      if (this.mode === "exam") return "Exam-type result comparison";
      if (this.mode === "session") return "Session-wise result comparison";
      return "Campus-wise result comparison";
    },
    reportContextLine() {
      const exam = this.resolveExamLabel();
      const session = this.resolveSessionLabel();
      const compared = (this.dimensions || []).join(" / ") || "-";
      const bits = [];
      if (exam) bits.push(exam);
      if (session) bits.push(`Session ${session}`);
      bits.push(compared);
      return bits.join("  |  ");
    },
    dimensionStats() {
      return this.buildDimensionStats(this.dimensions, this.subjects);
    },
    displayKpis() {
      const stats = this.dimensionStats;
      const byAvg = stats.length ? [...stats].sort((a, b) => b.avg - a.avg) : [];
      const base = { ...this.kpis };
      return {
        overallAverage: base.overallAverage,
        passPercent: base.passPercent,
        studentCount: base.studentCount,
        topLabel: base.topLabel || byAvg[0]?.dim || "",
        bottomLabel: base.bottomLabel || byAvg[byAvg.length - 1]?.dim || "",
        topAvg: byAvg[0]?.avg ?? 0,
        bottomAvg: byAvg[byAvg.length - 1]?.avg ?? 0,
      };
    },
    smartInsights() {
      if (!this.demoMode && Array.isArray(this.insights) && this.insights.length) {
        return this.insights.slice(0, 5).map((text, idx) => ({
          tone: idx === 0 ? "good" : idx === 1 ? "warn" : "info",
          icon: idx === 0 ? "TrendingUpIcon" : "ZapIcon",
          title: idx === 0 ? "Insight" : "Note",
          text: String(text),
        }));
      }
      return this.buildSmartInsights(this.dimensionStats, this.subjects);
    },
    scoreCards() {
      const k = this.displayKpis;
      return [
        {
          key: "avg",
          label: "Overall average",
          value: this.formatNum(k.overallAverage),
          hint: "Mean across compared set",
          icon: "AwardIcon",
          tone: "ink",
        },
        {
          key: "pass",
          label: "Pass rate",
          value: `${this.formatNum(k.passPercent)}%`,
          hint: "Pass rate from comparison",
          icon: "CheckCircleIcon",
          tone: "good",
        },
        {
          key: "top",
          label: "Top by average",
          value: k.topLabel || "-",
          hint: k.topLabel
            ? `Avg ${this.formatNum(k.topAvg)}`
            : "Best subject average",
          icon: "TrendingUpIcon",
          tone: "accent",
        },
        {
          key: "low",
          label: "Needs support",
          value: k.bottomLabel || "-",
          hint: k.bottomLabel
            ? `Lowest avg ${this.formatNum(k.bottomAvg)}`
            : "Lowest subject average",
          icon: "TrendingDownIcon",
          tone: "warn",
        },
        {
          key: "students",
          label: "Students covered",
          value: (k.studentCount || 0).toLocaleString(),
          hint: "In this comparison",
          icon: "UsersIcon",
          tone: "muted",
        },
      ];
    },
    subjectCategories() {
      return (this.subjects || []).map((s) => s.subjectName || s.name || "-");
    },
    passRankLabels() {
      return [...this.dimensionStats]
        .sort((a, b) => b.pass - a.pass)
        .map((s) => s.dim);
    },
    seriesLegendItems() {
      return (this.dimensions || []).map((name, i) => ({
        name,
        short: this.shortLabel(name, 20),
        color: SERIES_COLORS[i % SERIES_COLORS.length],
      }));
    },
    barOptions() {
      return buildGroupedBarOptions(this.subjectCategories);
    },
    barSeries() {
      return toGroupedBarSeries({
        dimensions: this.dimensions,
        subjects: this.subjects,
      });
    },
    rankOptions() {
      return buildPassRankBarOptions(this.passRankLabels);
    },
    rankSeries() {
      return toPassRankSeries({
        dimensions: this.passRankLabels,
        subjects: this.subjects,
      });
    },
    tableRows() {
      return this.subjects || [];
    },
  },
  watch: {
    campusID() {
      this.ensureModeAllowed();
      this.loadLookups().then(async () => {
        this.seedDefaultSelection();
        await this.refreshSubjectOptions();
      });
      this.ensureCampusesLoaded();
    },
    isHeadOffice() {
      this.ensureModeAllowed();
      this.ensureCampusesLoaded();
    },
    campuses: {
      immediate: false,
      handler() {
        if (Array.isArray(this.campuses) && this.campuses.length) {
          this.localCampuses = [];
        } else {
          this.ensureCampusesLoaded();
        }
      },
    },
    selectedClassIDs: {
      deep: true,
      handler() {
        this.refreshSubjectOptions();
      },
    },
  },
  async created() {
    this.ensureModeAllowed();
    await Promise.all([this.loadLookups(), this.ensureCampusesLoaded()]);
    this.seedDefaultSelection();
    await this.refreshSubjectOptions();
    if (this.canCompare) await this.runCompare();
  },
  methods: {
    ...mapActions({ get: "get" }),

    asIdArray(val) {
      if (val == null || val === "") return [];
      if (Array.isArray(val)) return val.filter((v) => v != null && v !== "");
      return [val];
    },

    ensureModeAllowed() {
      if (this.mode === "campus" && !this.canShowCampusMode) {
        this.mode = "class";
        this.selectedCampusIDs = [];
      }
    },

    async ensureCampusesLoaded() {
      if (!this.canShowCampusMode) return;
      if (Array.isArray(this.campuses) && this.campuses.length) return;
      if (Array.isArray(this.localCampuses) && this.localCampuses.length) return;
      await this.loadCampusesLocally();
    },

    async loadCampusesLocally() {
      this.campusesLoading = true;
      try {
        const cID =
          this.campusID || this.$store.state.userData.cId || 0;
        const response = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            cID,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];
        this.localCampuses = list
          .filter((c) => c && Number(c.id) > 0)
          .map((c) => ({
            ...c,
            branch: c.branch || c.name || `Campus ${c.id}`,
          }));
      } catch (e) {
        console.error("Result comparison campuses failed:", e);
        this.localCampuses = [];
      } finally {
        this.campusesLoading = false;
      }
    },

    resolveExamLabel() {
      const ids = this.asIdArray(this.selectedExamTypeIDs);
      if (!ids.length) return "";
      return ids
        .map((id) => {
          const hit = this.examTypeOptions.find((e) => e.id === id);
          return hit ? hit.examType || hit.name : null;
        })
        .filter(Boolean)
        .join(", ");
    },

    resolveSessionLabel() {
      const ids = this.asIdArray(this.selectedSessionIDs);
      if (!ids.length) return "";
      return ids
        .map((id) => {
          const hit = this.sessionOptions.find((s) => s.id === id);
          return hit ? hit.session || hit.name : null;
        })
        .filter(Boolean)
        .join(", ");
    },

    seedDefaultSelection() {
      if (!this.classOptions.length) return;
      if (this.mode === "class") {
        this.selectedClassIDs = this.classOptions.slice(0, 3).map((c) => c.id);
        this.selectedExamTypeIDs = this.firstExamTypeId();
        this.selectedSessionIDs = this.firstSessionId();
      } else if (this.mode === "exam") {
        this.selectedClassIDs = this.classOptions.slice(0, 1).map((c) => c.id);
        this.selectedExamTypeIDs = this.examTypeOptions
          .slice(0, 3)
          .map((e) => e.id);
        this.selectedSessionIDs = this.firstSessionId();
      } else if (this.mode === "session") {
        this.selectedClassIDs = this.classOptions.slice(0, 1).map((c) => c.id);
        this.selectedExamTypeIDs = this.firstExamTypeId();
        this.selectedSessionIDs = this.sessionOptions
          .slice(0, 2)
          .map((s) => s.id);
      } else {
        this.selectedCampusIDs = this.campusOptions.slice(0, 3).map((c) => c.id);
        this.selectedClassIDs = this.classOptions.slice(0, 1).map((c) => c.id);
        this.selectedExamTypeIDs = this.firstExamTypeId();
        this.selectedSessionIDs = this.firstSessionId();
      }
      this.selectedSubjectIDs = [];
    },

    async switchMode(id) {
      if (this.mode === id) return;
      if (id === "campus" && !this.canShowCampusMode) return;
      this.mode = id;
      this.error = null;
      this.hasResult = false;
      if (id === "campus") await this.ensureCampusesLoaded();
      this.seedDefaultSelection();
      await this.refreshSubjectOptions();
      if (this.canCompare) this.runCompare();
    },

    firstExamTypeId() {
      return this.examTypeOptions[0] ? this.examTypeOptions[0].id : null;
    },
    firstSessionId() {
      const current = this.sessionOptions.find(
        (s) => s.isCurrent || s.current || s.is_current
      );
      if (current) return current.id;
      return this.sessionOptions[0] ? this.sessionOptions[0].id : null;
    },

    async loadLookups() {
      this.lookupsLoading = true;
      this.demoMode = false;
      try {
        const cID = this.campusID || this.$store.state.userData.cId || 0;
        const db = this.$store.state.userData.db;
        const token = this.$store.state.userData.token;

        const [classesRes, examsRes, sessionsRes, subjectsRes] =
          await Promise.all([
          this.get({
            url:
              this.$store.state.domain +
              "myclasses/LoadClasses?db=" +
              db +
              "&cId=" +
              cID +
              "&dID=0",
            token,
          }),
          this.get({
            url:
              this.$store.state.domain +
              "ExamTypes/LoadData?db=" +
              db +
              "&cID=" +
              cID +
              "&status=active&page=1&pageSize=1000",
            token,
          }),
          this.get({
            url:
              this.$store.state.domain +
              "Sessions?db=" +
              db +
              "&cID=" +
              cID,
            token,
          }),
          this.get({
            url:
              this.$store.state.domain +
              "Subjects?db=" +
              db +
              "&cID=" +
              cID,
            token,
          }),
        ]);

        const classList = Array.isArray(classesRes)
          ? classesRes
          : classesRes?.data || [];
        this.classOptions = classList
          .map((el) => el.cls || el)
          .filter((c) => c && c.id != null);

        const examData = examsRes?.data || examsRes;
        this.examTypeOptions = Array.isArray(examData) ? examData : [];

        const sessionData = sessionsRes?.data || sessionsRes;
        this.sessionOptions = Array.isArray(sessionData) ? sessionData : [];

        const subjectList = Array.isArray(subjectsRes)
          ? subjectsRes
          : subjectsRes?.data || [];
        this.campusSubjectPool = Array.isArray(subjectList) ? subjectList : [];
      } catch (e) {
        console.error("Result comparison lookups failed:", e);
        this.classOptions = [];
        this.examTypeOptions = [];
        this.sessionOptions = [];
        this.subjectOptions = [];
        this.campusSubjectPool = [];
      } finally {
        this.lookupsLoading = false;
      }
    },

    normalizeSubjectOption(raw) {
      if (!raw || typeof raw !== "object") return null;
      const id = raw.id ?? raw.subjectID ?? raw.subjectId ?? raw.sID;
      if (id == null || Number(id) === 0) return null;
      const name = String(
        raw.name ?? raw.subjectName ?? raw.subject ?? raw.title ?? ""
      ).trim();
      if (!name) return null;
      return { id, name };
    },

    mergeSubjectLists(map, list) {
      (Array.isArray(list) ? list : []).forEach((item) => {
        const normalized = this.normalizeSubjectOption(item);
        if (normalized) map.set(normalized.id, normalized);
      });
    },

    async refreshSubjectOptions() {
      this.subjectsLoading = true;
      try {
        const map = new Map();
        const cID = this.campusID || this.$store.state.userData.cId || 0;
        const db = this.$store.state.userData.db;
        const token = this.$store.state.userData.token;

        this.classOptions.forEach((cls) => {
          this.mergeSubjectLists(
            map,
            cls.subjects || cls.Subjects || cls.subjectList || []
          );
        });

        const classIds = this.asIdArray(this.selectedClassIDs);
        if (classIds.length && db && token) {
          const perClass = await Promise.all(
            classIds.map((clsID) =>
              this.get({
                url:
                  this.$store.state.domain +
                  "Subjects/LoadSubjectClassData?db=" +
                  db +
                  "&cID=" +
                  cID +
                  "&clsID=" +
                  clsID,
                token,
              }).catch((err) => {
                console.warn(
                  "LoadSubjectClassData failed for class",
                  clsID,
                  err
                );
                return [];
              })
            )
          );
          perClass.forEach((res) => {
            const list = Array.isArray(res) ? res : res?.data || [];
            this.mergeSubjectLists(map, list);
          });
        }

        if (!map.size) {
          this.mergeSubjectLists(map, this.campusSubjectPool);
        }

        this.subjectOptions = Array.from(map.values()).sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );

        const validIds = new Set(this.subjectOptions.map((s) => s.id));
        this.selectedSubjectIDs = this.asIdArray(
          this.selectedSubjectIDs
        ).filter((id) => validIds.has(id));
      } catch (e) {
        console.error("Result comparison subject options failed:", e);
        this.subjectOptions = [];
      } finally {
        this.subjectsLoading = false;
      }
    },

    formatNum(v) {
      if (v == null || v === "" || Number.isNaN(Number(v))) return "-";
      return Number(v).toFixed(1).replace(/\.0$/, "");
    },

    shortLabel(value, max = 20) {
      const s = String(value == null ? "" : value).trim();
      if (!s) return "-";
      if (s.length <= max) return s;
      return `${s.slice(0, Math.max(1, max - 1))}…`;
    },

    cellValue(row, dim, field) {
      const series = row.series || [];
      const hit = series.find((s) => s.key === dim || s.label === dim);
      return hit ? hit[field] : null;
    },

    buildDimensionStats(dimensions = [], subjects = []) {
      return (dimensions || []).map((dim) => {
        let avgSum = 0;
        let passSum = 0;
        let n = 0;
        let count = 0;
        (subjects || []).forEach((subj) => {
          const row = (subj.series || []).find(
            (s) => s.key === dim || s.label === dim
          );
          if (!row || !(Number(row.count) > 0)) return;
          avgSum += Number(row.average) || 0;
          passSum += Number(row.passPercent) || 0;
          n += 1;
          count = Math.max(count, Number(row.count) || 0);
        });
        return {
          dim,
          avg: n ? avgSum / n : 0,
          pass: n ? passSum / n : 0,
          count,
          n,
        };
      });
    },

    buildSmartInsights(stats = [], subjects = []) {
      if (!stats.length || !subjects.length) return [];

      const byAvg = [...stats].sort((a, b) => b.avg - a.avg);
      const byPass = [...stats].sort((a, b) => b.pass - a.pass);
      const topAvg = byAvg[0];
      const bottomAvg = byAvg[byAvg.length - 1];
      const topPass = byPass[0];
      const bottomPass = byPass[byPass.length - 1];
      const tips = [];

      if (topAvg) {
        tips.push({
          tone: "good",
          icon: "TrendingUpIcon",
          title: "Top by average",
          text: `${topAvg.dim} leads with avg ${this.formatNum(
            topAvg.avg
          )} across subjects.`,
        });
      }

      if (bottomAvg && topAvg && bottomAvg.dim !== topAvg.dim) {
        tips.push({
          tone: "warn",
          icon: "AlertTriangleIcon",
          title: "Needs academic support",
          text: `${bottomAvg.dim} has the lowest average (${this.formatNum(
            bottomAvg.avg
          )}). Focus teaching support here.`,
        });
      }

      if (
        bottomPass &&
        bottomAvg &&
        bottomPass.dim !== bottomAvg.dim
      ) {
        tips.push({
          tone: "info",
          icon: "CheckCircleIcon",
          title: "Pass rate is different",
          text: `${bottomPass.dim} has the lowest pass rate (${this.formatNum(
            bottomPass.pass
          )}%), while ${bottomAvg.dim} is lowest on average.`,
        });
      } else if (bottomPass && topPass && bottomPass.dim !== topPass.dim) {
        tips.push({
          tone: "info",
          icon: "CheckCircleIcon",
          title: "Pass rate ranking",
          text: `${topPass.dim} tops pass rate at ${this.formatNum(
            topPass.pass
          )}%. ${bottomPass.dim} is lowest at ${this.formatNum(
            bottomPass.pass
          )}%.`,
        });
      }

      // Biggest subject gap between best and weakest dimension
      let bestGap = null;
      subjects.forEach((subj) => {
        const rows = (subj.series || [])
          .map((s) => ({
            key: s.key || s.label,
            average: Number(s.average),
          }))
          .filter((r) => r.key && !Number.isNaN(r.average));
        if (rows.length < 2) return;
        const hi = [...rows].sort((a, b) => b.average - a.average)[0];
        const lo = [...rows].sort((a, b) => a.average - b.average)[0];
        const gap = hi.average - lo.average;
        if (!bestGap || gap > bestGap.gap) {
          bestGap = {
            gap,
            subject: subj.subjectName || subj.name || "Subject",
            hi: hi.key,
            lo: lo.key,
            hiAvg: hi.average,
            loAvg: lo.average,
          };
        }
      });

      if (bestGap && bestGap.gap >= 5) {
        tips.push({
          tone: "accent",
          icon: "ZapIcon",
          title: "Biggest subject gap",
          text: `${bestGap.subject}: ${bestGap.hi} (${this.formatNum(
            bestGap.hiAvg
          )}) leads ${bestGap.lo} (${this.formatNum(
            bestGap.loAvg
          )}) by ${this.formatNum(bestGap.gap)} pts.`,
        });
      }

      // Weakest subject for the lowest-average dimension
      if (bottomAvg) {
        let weakSubj = null;
        subjects.forEach((subj) => {
          const row = (subj.series || []).find(
            (s) => s.key === bottomAvg.dim || s.label === bottomAvg.dim
          );
          if (!row) return;
          const avg = Number(row.average);
          if (Number.isNaN(avg)) return;
          if (!weakSubj || avg < weakSubj.avg) {
            weakSubj = {
              avg,
              name: subj.subjectName || subj.name || "Subject",
            };
          }
        });
        if (weakSubj) {
          tips.push({
            tone: "muted",
            icon: "BookOpenIcon",
            title: "Priority subject",
            text: `For ${bottomAvg.dim}, start with ${
              weakSubj.name
            } (avg ${this.formatNum(weakSubj.avg)}).`,
          });
        }
      }

      return tips.slice(0, 5);
    },

    passBandClass(pct) {
      const n = Number(pct);
      if (Number.isNaN(n)) return "rci-pass-pill--muted";
      if (n >= 80) return "rci-pass-pill--good";
      if (n >= 50) return "rci-pass-pill--mid";
      return "rci-pass-pill--low";
    },

    buildQueryParams() {
      const db = this.$store.state.userData.db;
      const campusID = this.campusID || this.$store.state.userData.cId || 0;
      const params = {
        db,
        campusID,
        mode: this.mode,
        classIDs: this.asIdArray(this.selectedClassIDs).join(","),
        examTypeIDs: this.asIdArray(this.selectedExamTypeIDs).join(","),
        sessionIDs: this.asIdArray(this.selectedSessionIDs).join(","),
        campusIDs: this.asIdArray(this.selectedCampusIDs).join(","),
        subjectIDs: this.asIdArray(this.selectedSubjectIDs).join(","),
      };
      return params;
    },

    normalizePayload(raw) {
      const data = raw?.data?.data || raw?.data || raw || {};
      const subjects = Array.isArray(data.subjects) ? data.subjects : [];
      const dimensions = Array.isArray(data.dimensions)
        ? data.dimensions
        : [];
      const kpis = { ...emptyKpis(), ...(data.kpis || {}) };
      const insights = Array.isArray(data.insights) ? data.insights : [];
      return { subjects, dimensions, kpis, insights };
    },

    buildMockPayload() {
      const subjectPool = this.asIdArray(this.selectedSubjectIDs).length
        ? this.subjectOptions.filter((s) =>
            this.asIdArray(this.selectedSubjectIDs).includes(s.id)
          )
        : this.subjectOptions.slice(0, 6);

      let dimensions = [];
      if (this.mode === "class") {
        dimensions = this.classOptions
          .filter((c) => this.asIdArray(this.selectedClassIDs).includes(c.id))
          .map((c) => c.name);
      } else if (this.mode === "exam") {
        dimensions = this.examTypeOptions
          .filter((e) => this.asIdArray(this.selectedExamTypeIDs).includes(e.id))
          .map((e) => e.examType || e.name);
      } else if (this.mode === "session") {
        dimensions = this.sessionOptions
          .filter((s) => this.asIdArray(this.selectedSessionIDs).includes(s.id))
          .map((s) => s.session || s.name);
      } else {
        dimensions = this.campusOptions
          .filter((c) => this.asIdArray(this.selectedCampusIDs).includes(c.id))
          .map((c) => c.branch || c.name);
      }

      const subjects = subjectPool.map((subj, si) => ({
        subjectID: subj.id,
        subjectName: subj.name,
        series: dimensions.map((dim, di) => {
          const average = Math.round(
            55 + ((si * 7 + di * 11) % 35) + (di % 3) * 2
          );
          const passPercent = Math.min(
            98,
            Math.round(45 + average * 0.45 + ((si + di) % 8))
          );
          return {
            key: dim,
            average,
            passPercent,
            max: Math.min(100, average + 15),
            min: Math.max(0, average - 25),
            count: 28 + ((si + di) % 12),
          };
        }),
      }));

      // KPIs
      let sumAvg = 0;
      let sumPass = 0;
      let cells = 0;
      let studentCount = 0;
      const dimScores = dimensions.map((dim) => {
        let t = 0;
        let n = 0;
        subjects.forEach((s) => {
          const row = s.series.find((x) => x.key === dim);
          if (row) {
            t += row.average;
            n += 1;
            sumAvg += row.average;
            sumPass += row.passPercent;
            cells += 1;
            studentCount += row.count;
          }
        });
        return { dim, avg: n ? t / n : 0 };
      });
      dimScores.sort((a, b) => b.avg - a.avg);

      const insights = [];
      if (dimScores.length >= 2 && subjects[0]) {
        const lead = dimScores[0];
        const trail = dimScores[dimScores.length - 1];
        const math = subjects[0];
        const leadCell = math.series.find((s) => s.key === lead.dim);
        const trailCell = math.series.find((s) => s.key === trail.dim);
        if (leadCell && trailCell) {
          const gap = Math.round(leadCell.average - trailCell.average);
          if (gap > 0) {
            insights.push(
              `${lead.dim} leads ${math.subjectName} by ${gap} pts vs ${trail.dim}`
            );
          }
        }
        insights.push(
          `${lead.dim} ranks #1 overall | ${trail.dim} needs focus`
        );
      }

      return {
        kpis: {
          overallAverage: cells ? sumAvg / cells : 0,
          passPercent: cells ? sumPass / cells : 0,
          studentCount,
          topLabel: dimScores[0]?.dim || "",
          bottomLabel: dimScores[dimScores.length - 1]?.dim || "",
        },
        dimensions,
        subjects,
        insights,
      };
    },

    applyResultPayload(payload) {
      this.dimensions = payload.dimensions || [];
      this.subjects = payload.subjects || [];
      const stats = this.buildDimensionStats(this.dimensions, this.subjects);
      const byAvg = [...stats].sort((a, b) => b.avg - a.avg);
      const apiKpis = { ...emptyKpis(), ...(payload.kpis || {}) };
      this.kpis = {
        overallAverage: Number(apiKpis.overallAverage) || 0,
        passPercent: Number(apiKpis.passPercent) || 0,
        studentCount: Number(apiKpis.studentCount) || 0,
        topLabel: apiKpis.topLabel || byAvg[0]?.dim || "",
        bottomLabel:
          apiKpis.bottomLabel || byAvg[byAvg.length - 1]?.dim || "",
      };
      this.insights = Array.isArray(payload.insights) ? payload.insights : [];
      this.hasResult = true;
      this.lastRefreshed = new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    async runCompare() {
      if (!this.canCompare) return;
      this.loading = true;
      this.error = null;
      this.demoMode = false;

      try {
        const params = this.buildQueryParams();
        const qs = Object.entries(params)
          .filter(([, v]) => v !== "" && v != null)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join("&");
        const res = await this.get({
          url:
            this.$store.state.domain + "Marks/LoadResultComparison?" + qs,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        if (
          res &&
          (res.status === "success" ||
            res.subjects ||
            res.data?.subjects ||
            res.data?.data?.subjects)
        ) {
          this.applyResultPayload(this.normalizePayload(res));
        } else {
          this.hasResult = false;
          this.error = "No comparison data returned for the selected filters.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Could not load comparison data. Please try again.";
        this.hasResult = false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.rci-card {
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 18px rgba(115, 103, 240, 0.06);
}

.rci-card-header {
  padding: 1.2rem 1.35rem 1.05rem;
  border-bottom: 1px solid #f0edff;
  background: linear-gradient(180deg, #fbfaff 0%, #fff 100%);
}

.rci-body {
  padding: 1.15rem 1.35rem 1.4rem;
}

.rci-header-icon {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(145deg, #f3f0ff, #ebe6ff);
  color: #7367f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);

  &__ring {
    position: absolute;
    inset: -3px;
    border-radius: 16px;
    border: 1px solid rgba(115, 103, 240, 0.18);
    pointer-events: none;
  }
}

.rci-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d1b69;
}

.rci-subtitle {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #6e6b7b;
}

.rci-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;

  &--demo {
    background: #fff4e6;
    color: #ff9f43;
    border: 1px solid #ffe0b8;
  }

  &--live {
    background: #e8f9f0;
    color: #28c76f;
    border: 1px solid #c8efd9;
  }
}

.rci-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
}

.rci-meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #faf9ff;
  border: 1px solid #ebe9f1;
  color: #6e6b7b;
  font-size: 0.72rem;
  font-weight: 600;

  &--mode {
    background: #f3f0ff;
    border-color: #e0d8ff;
    color: #7367f0;
  }
}

.rci-mode-switch {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.35rem;
  background: #faf9ff;
  border: 1px solid #e0d8ff;
  border-radius: 14px;
  margin-bottom: 1rem !important;
}

.rci-mode-btn {
  border: 0;
  background: transparent;
  color: #6e6b7b;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem 0.35rem 0.35rem;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &__icon {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    color: #6e6b7b;
    border: 1px solid #ebe9f1;
    transition: all 0.2s ease;
  }

  &:hover {
    background: #fff;
    color: #5e5873;
  }

  &--active {
    background: #fff;
    color: #7367f0;
    box-shadow: 0 2px 10px rgba(115, 103, 240, 0.14);

    .rci-mode-btn__icon {
      background: #f3f0ff;
      border-color: #e0d8ff;
      color: #7367f0;
    }
  }
}

.rci-filter-bar {
  padding: 1rem 1.05rem 1.05rem;
  margin-bottom: 1rem !important;
  background: linear-gradient(180deg, #fbfbfc 0%, #faf9ff 100%);
  border: 1px solid #f0edff;
  border-radius: 14px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.35rem;
    margin-bottom: 0.85rem;
    padding-bottom: 0.7rem;
    border-bottom: 1px dashed #e8e4f5;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    font-size: 0.82rem;
    font-weight: 700;
    color: #5e5873;
  }

  &__hint {
    color: #b9b9c3;
    font-size: 0.72rem;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
    align-items: flex-end;
  }
}

.rci-filter-item {
  flex: 1 1 150px;
  min-width: 140px;
  max-width: 230px;

  &--grow {
    flex: 1.35 1 210px;
    max-width: 300px;
  }

  &--action {
    flex: 0 0 auto;
    max-width: none;
    min-width: auto;
  }
}

.rci-filter-label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a5a3ae;

  &--spacer {
    visibility: hidden;
  }
}

.rci-select {
  ::v-deep .vs__dropdown-toggle {
    min-height: 38px;
    border-radius: 10px;
    border-color: #e0d8ff;
    background: #fff;
  }

  ::v-deep .vs--open .vs__dropdown-toggle,
  ::v-deep .vs__dropdown-toggle:hover {
    border-color: #b8a9ff;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
  }
}

.rci-compare-btn {
  min-height: 38px;
  border-radius: 10px;
  font-weight: 700;
  padding-left: 1.15rem;
  padding-right: 1.15rem;
}

.rci-hint {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  color: #ff9f43;
  font-weight: 600;
  padding: 0.55rem 0.8rem;
  border-radius: 10px;
  background: #fff8f0;
  border: 1px solid #ffe6c7;
}

.rci-state {
  text-align: center;
  padding: 2.6rem 1.25rem;
  border-radius: 16px;
  border: 1px dashed #e0d8ff;
  background: linear-gradient(180deg, #fbfaff 0%, #f7f5ff 100%);

  &__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 0.9rem;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #e0d8ff;
    color: #7367f0;
    box-shadow: 0 8px 20px rgba(115, 103, 240, 0.1);
  }

  &__title {
    margin-bottom: 0.35rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: #5e5873;
  }

  &__sub {
    margin-bottom: 0;
    font-size: 0.84rem;
    color: #6e6b7b;
  }

  &__steps {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.55rem;
    margin-top: 1.1rem;

    span {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.35rem 0.7rem;
      border-radius: 999px;
      background: #fff;
      border: 1px solid #ebe9f1;
      color: #6e6b7b;
      font-size: 0.72rem;
      font-weight: 600;
    }
  }

  &--error {
    border-color: #f5c6c6;
    background: linear-gradient(180deg, #fffafa 0%, #fff5f5 100%);

    .rci-state__icon {
      color: #ea5455;
      border-color: #f5c6c6;
      box-shadow: 0 8px 20px rgba(234, 84, 85, 0.1);
    }

    .rci-state__title {
      color: #ea5455;
    }

    .rci-state__sub {
      margin-bottom: 0.85rem;
    }
  }
}

.rci-skel-block {
  border-radius: 12px;
  background: linear-gradient(90deg, #f3f0ff, #faf9ff, #f3f0ff);
  background-size: 200% 100%;
  animation: rciPulse 1.3s ease-in-out infinite;
}

@keyframes rciPulse {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.rci-anim-in {
  animation: rciIn 0.4s ease;
}

@keyframes rciIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.rci-summary-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid #f0edff;
  background: linear-gradient(120deg, #fbfaff 0%, #fff 55%, #faf9ff 100%);
}

.rci-summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f0ff;
  color: #7367f0;
  border: 1px solid #e0d8ff;
  flex-shrink: 0;
}

.rci-summary-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2d1b69;
}

.rci-summary-context {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.84rem;
  color: #6e6b7b;
  line-height: 1.45;
}

.rci-summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.rci-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #f3f0ff;
  border: 1px solid #e0d8ff;
  color: #7367f0;
  font-size: 0.72rem;
  font-weight: 700;

  &--soft {
    background: #fff;
    color: #6e6b7b;
    border-color: #ebe9f1;
  }
}

.rci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.rci-kpi-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 0.95rem;
  border-radius: 14px;
  border: 1px solid #ebe9f1;
  background: #fff;
  box-shadow: 0 2px 8px rgba(45, 27, 105, 0.03);
  animation: rciRise 0.4s ease both;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #e0d8ff;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(115, 103, 240, 0.1);
  }

  &--good::before {
    background: #28c76f;
  }
  &--warn::before {
    background: #ff9f43;
  }
  &--ink::before,
  &--accent::before {
    background: #7367f0;
  }
  &--muted::before {
    background: #b9b9c3;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--ink,
    &--accent {
      background: #f3f0ff;
      color: #7367f0;
    }
    &--good {
      background: #e8f9f0;
      color: #28c76f;
    }
    &--warn {
      background: #fff4e6;
      color: #ff9f43;
    }
    &--muted {
      background: #f1f0f5;
      color: #6e6b7b;
    }
  }

  &__label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #b9b9c3;
    margin-bottom: 0.2rem;
  }

  &__value {
    font-size: 1.12rem;
    font-weight: 700;
    color: #5e5873;
    line-height: 1.2;
    word-break: break-word;
  }

  &__hint {
    margin-top: 0.2rem;
    font-size: 0.68rem;
    color: #b9b9c3;
    font-weight: 500;
  }
}

@keyframes rciRise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.rci-insights-block {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid #e0d8ff;
  background: #faf9ff;

  &__head {
    display: inline-flex;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: #7367f0;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}

.rci-insights-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.rci-insight-card {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.75rem 0.85rem;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e8e4f5;
  animation: rciRise 0.35s ease both;

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #f3f0ff;
    color: #7367f0;
  }

  &__title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #9ca3af;
    margin-bottom: 0.2rem;
  }

  &__text {
    font-size: 0.82rem;
    font-weight: 600;
    color: #374151;
    line-height: 1.4;
  }

  &--good .rci-insight-card__icon {
    background: #e8f9f0;
    color: #28c76f;
  }
  &--warn .rci-insight-card__icon {
    background: #fff4e6;
    color: #ff9f43;
  }
  &--info .rci-insight-card__icon {
    background: #e6f7fb;
    color: #00cfe8;
  }
  &--accent .rci-insight-card__icon {
    background: #f3f0ff;
    color: #7367f0;
  }
  &--muted .rci-insight-card__icon {
    background: #f1f0f5;
    color: #6e6b7b;
  }
}

@media (max-width: 768px) {
  .rci-insights-grid {
    grid-template-columns: 1fr;
  }
}

.rci-series-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.15rem 0 0.65rem;
}

.rci-series-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  padding: 0.28rem 0.55rem 0.28rem 0.4rem;
  border-radius: 999px;
  background: #f8f9fb;
  border: 1px solid #eef0f4;
  color: #374151;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: default;

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 9.5rem;
  }
}

.rci-charts-grid {
  display: grid;
  grid-template-columns: 1.65fr 1fr;
  gap: 0.9rem;
  margin-bottom: 0.9rem;
}

.rci-panel {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  padding: 1rem 1rem 0.5rem;
  box-shadow: 0 2px 10px rgba(45, 27, 105, 0.03);

  &--table {
    padding: 0;
    overflow: hidden;
  }

  &__head {
    margin-bottom: 0.55rem;

    &--table {
      padding: 0.95rem 1rem 0.75rem;
      border-bottom: 1px solid #f0edff;
      margin-bottom: 0;
      background: #faf9ff;
    }
  }

  &__badge {
    width: 36px;
    height: 36px;
    border-radius: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--primary {
      background: #f3f0ff;
      color: #7367f0;
    }
    &--success {
      background: #e8f9f0;
      color: #28c76f;
    }
    &--ink {
      background: #f1f0f5;
      color: #5e5873;
    }
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2d1b69;
  }

  &__sub {
    color: #b9b9c3;
    font-size: 0.75rem;
  }
}

.rci-anim-rise {
  animation: rciRise 0.45s ease both;

  &--delay {
    animation-delay: 0.08s;
  }

  &--delay2 {
    animation-delay: 0.14s;
  }
}

.rci-table-scroll {
  overflow-x: auto;
}

.rci-table {
  width: 100%;
  min-width: 540px;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #f0edff;
    padding: 0.7rem 0.8rem;
    font-size: 0.84rem;
    white-space: nowrap;
  }

  thead th {
    background: #f3f0ff;
    color: #5e5873;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .rci-subhead {
    background: #faf9ff !important;
    color: #6e6b7b !important;
    font-size: 0.65rem !important;
  }

  tbody tr:nth-child(even) td {
    background: #fafafa;
  }

  tbody tr:hover td {
    background: #faf9ff;
  }

  .rci-sticky-col {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    min-width: 120px;
  }

  thead .rci-sticky-col {
    background: #f3f0ff;
    z-index: 2;
  }
}

.rci-table-row {
  animation: rciRise 0.3s ease both;
}

.rci-th-with-icon {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  &--center {
    justify-content: center;
  }
}

.rci-subject-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.rci-subject-dot {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f0ff;
  color: #7367f0;
  flex-shrink: 0;
}

.rci-subject-name {
  font-weight: 700;
  color: #5e5873;
}

.rci-avg-cell {
  font-weight: 700;
  color: #7367f0;
  font-variant-numeric: tabular-nums;
}

.rci-pass-pill {
  display: inline-block;
  min-width: 50px;
  padding: 0.2rem 0.45rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  &--good {
    background: #e8f9f0;
    color: #28c76f;
  }
  &--mid {
    background: #fff4e6;
    color: #ff9f43;
  }
  &--low {
    background: #fef0f0;
    color: #ea5455;
  }
  &--muted {
    background: #f1f0f5;
    color: #b9b9c3;
  }
}

@media (max-width: 1200px) {
  .rci-kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .rci-charts-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .rci-filter-item,
  .rci-filter-item--grow,
  .rci-filter-item--action {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }

  .rci-compare-btn {
    width: 100%;
  }

  .rci-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rci-header-meta {
    width: 100%;
    justify-content: flex-start;
    margin-top: 0.35rem;
  }
}
</style>
